import {
    AGED_ORDER_MS,
    NEXT_DAY_CARRIERS,
    NEXT_DAY_METHOD_PATTERN,
    ORDER_URGENCY,
    ORDER_URGENCY_STYLES,
    UNSHIPPED_STATUS_IDS
} from '../global/orderUrgency';

const NEXT_DAY_CARRIER_NAMES = NEXT_DAY_CARRIERS.map(name => name.toLowerCase());

/**
 * Whether an order is on a next-day-air service: one of the next-day carriers
 * (case-insensitive, exact) with a service name matching the next-day pattern.
 * Matched by name rather than shipping_type id because ids differ between
 * environments.
 *
 * @param {Object} order a list row carrying shipped_with_name and shipping_type_name
 * @return {boolean}
 */
export const isNextDayService = (order) => {
    const carrier = String(order.shipped_with_name || '').trim().toLowerCase();
    const method = String(order.shipping_type_name || '');
    return NEXT_DAY_CARRIER_NAMES.includes(carrier) && NEXT_DAY_METHOD_PATTERN.test(method);
};

/**
 * The epoch time an order was created, or null when it has no usable date.
 *
 * @param {Object} order
 * @return {number|null}
 */
const orderCreatedTime = (order) => {
    if (!order.created_on) {
        return null;
    }
    const time = new Date(order.created_on).getTime();
    return (isNaN(time)) ? null : time;
};

/**
 * Classify an Orders list row for urgency highlighting (#331).
 *
 * Only orders in an unshipped status are considered. Of those, a next-day-air
 * order is NEXT_DAY however old it is; any other order is AGED once it is more
 * than AGED_ORDER_HOURS past its created_on. Everything else is null.
 *
 * @param {Object} order a list row carrying status_id, shipped_with_name,
 *                       shipping_type_name and created_on
 * @param {number} now   the reference time in ms since the epoch
 * @return {string|null} ORDER_URGENCY.NEXT_DAY, ORDER_URGENCY.AGED or null
 */
export const getOrderUrgency = (order, now = Date.now()) => {
    if (!order || !UNSHIPPED_STATUS_IDS.includes(Number(order.status_id))) {
        return null;
    }
    if (isNextDayService(order)) {
        return ORDER_URGENCY.NEXT_DAY;
    }
    const created = orderCreatedTime(order);
    if (created !== null && now - created > AGED_ORDER_MS) {
        return ORDER_URGENCY.AGED;
    }
    return null;
};

/**
 * The inline style for an Orders list row, or undefined when it needs none.
 *
 * @param {Object} order
 * @param {number} now
 * @return {Object|undefined}
 */
export const getOrderUrgencyRowStyle = (order, now = Date.now()) => {
    const urgency = getOrderUrgency(order, now);
    return (urgency) ? ORDER_URGENCY_STYLES[urgency] : undefined;
};
